import React from 'react';
import AdminSidebar from './components/AdminSidebar';
import Header from '../teacher_screen/components/Header';
import { useLanguage } from '../../../context/LanguageContext';
import {
    Users,
    GraduationCap,
    BookOpen,
    DollarSign,
    TrendingUp,
    MoreHorizontal,
    Calendar,
    Filter,
    Download,
    ChevronDown,
    Clock,
    Award,
    Zap,
    ArrowUpRight,
    ArrowDownRight
} from 'lucide-react';

export default function AdminDashboard() {
    const { t } = useLanguage();
    const stats = [
        { label: t('students'), value: '1,280', trend: '+45 recently', icon: Users, color: 'text-blue-500 bg-blue-50' },
        { label: t('teachers'), value: '84', trend: '02 on leave', icon: GraduationCap, color: 'text-emerald-500 bg-emerald-50' },
        { label: t('classes'), value: '42', trend: 'Across 12 Grades', icon: BookOpen, color: 'text-amber-500 bg-amber-50' },
        { label: 'Total Revenue', value: '$84,200', trend: '+12.5% vs MoM', icon: DollarSign, color: 'text-rose-500 bg-rose-50' },
    ];

    const recentAcitvities = [
        { id: 1, user: 'Dr. Sarah Johnson', action: 'Posted a new assignment', time: '10 mins ago', type: 'Teacher' },
        { id: 2, user: 'John Smith (Parent)', action: 'Paid term fees for Grade 8', time: '25 mins ago', type: 'Parent' },
        { id: 3, user: 'Michael Scott', action: 'Applied for sick leave', time: '40 mins ago', type: 'Student' },
        { id: 4, user: 'Admin System', action: 'Generated monthly payroll', time: '1 hour ago', type: 'System' },
    ];

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <AdminSidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title={t('adminDashboard')}
                    subtitle={t('adminDashboardSub')}
                >
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-100 rounded-xl text-[14px] font-black text-gray-700 shadow-sm cursor-pointer hover:bg-gray-50 transition-all group">
                        <Calendar size={18} className="text-blue-500 group-hover:scale-110 transition-transform" />
                        <span>{t('academicSession')}</span>
                    </div>
                </Header>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
                    {stats.map((stat, idx) => (
                        <div key={idx} className="bg-white p-6 rounded-[24px] border border-gray-100 shadow-sm hover:scale-[1.02] transition-all cursor-default group">
                            <div className="flex items-center justify-between mb-4">
                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${stat.color} group-hover:shadow-lg transition-all`}>
                                    <stat.icon size={22} strokeWidth={2.5} />
                                </div>
                                <div className="text-right">
                                    <span className="text-[11px] font-black text-gray-400 uppercase tracking-widest leading-none">{stat.label}</span>
                                    <h4 className="text-[28px] font-black text-[#111827] mt-1">{stat.value}</h4>
                                </div>
                            </div>
                            <div className="flex items-center gap-1.5 text-[12px] font-bold text-[#111827] opacity-60">
                                {stat.label === 'Total Revenue' ? <ArrowUpRight size={14} className="text-emerald-500" /> : <Zap size={14} className="text-blue-500" />}
                                {stat.trend}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Middle Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
                    <div className="lg:col-span-2 bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm flex flex-col min-h-[400px]">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h3 className="text-[20px] font-black text-[#111827]">{t('financialAnalytics')}</h3>
                                <p className="text-gray-400 text-[14px] font-medium">Fee collection vs Operational expenses</p>
                            </div>
                            <button className="p-2 text-gray-400"><MoreHorizontal size={20} /></button>
                        </div>
                        <div className="flex-1 flex items-end gap-4 h-48 border-b border-gray-100 pb-2">
                            {[40, 70, 45, 90, 65, 80, 55, 95].map((h, i) => (
                                <div key={i} className="flex-1 flex flex-col justify-end gap-1 group cursor-pointer">
                                    <div
                                        style={{ height: `${h}%` }}
                                        className="w-full bg-blue-500/10 rounded-t-lg group-hover:bg-blue-500 transition-all relative"
                                    >
                                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-[#111827] text-white text-[10px] font-bold px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                            ${h}k
                                        </div>
                                    </div>
                                    <span className="text-[10px] font-bold text-gray-400 text-center">M{i + 1}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white p-8 rounded-[32px] border border-gray-100 shadow-sm flex flex-col">
                        <h3 className="text-[20px] font-black text-[#111827] mb-6">{t('recentActivity')}</h3>
                        <div className="space-y-6 flex-1">
                            {recentAcitvities.map((act) => (
                                <div key={act.id} className="flex gap-4">
                                    <div className={`w-2 h-10 rounded-full shrink-0 ${act.type === 'Teacher' ? 'bg-blue-500' :
                                            act.type === 'Parent' ? 'bg-emerald-500' :
                                                act.type === 'Student' ? 'bg-amber-500' : 'bg-slate-300'
                                        }`}></div>
                                    <div>
                                        <p className="text-[14px] font-black text-[#111827] leading-none mb-1">{act.user}</p>
                                        <p className="text-[13px] font-medium text-gray-500 leading-tight mb-1">{act.action}</p>
                                        <span className="text-[11px] font-bold text-gray-400 uppercase tracking-tighter">{act.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button className="w-full mt-8 py-3 border border-gray-100 rounded-xl text-[13px] font-bold text-gray-600 hover:bg-gray-50 transition-all">
                            View System Audit Log
                        </button>
                    </div>
                </div>

                {/* Bottom Panels */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10 pb-10">
                    <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden p-8">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-[20px] font-black text-[#111827]">{t('quickActions')}</h3>
                            <ChevronDown size={20} className="text-gray-400" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <button className="p-4 bg-slate-50 rounded-2xl text-left hover:bg-blue-50 transition-all group">
                                <Plus size={20} className="mb-3 text-blue-500" />
                                <p className="text-[15px] font-black text-[#111827]">Add New Teacher</p>
                                <p className="text-[12px] font-bold text-gray-400">Onboard faculty members</p>
                            </button>
                            <button className="p-4 bg-slate-50 rounded-2xl text-left hover:bg-emerald-50 transition-all group">
                                <Award size={20} className="mb-3 text-emerald-500" />
                                <p className="text-[15px] font-black text-[#111827]">Manage Promotions</p>
                                <p className="text-[12px] font-bold text-gray-400">Student grade transitions</p>
                            </button>
                        </div>
                    </div>

                    <div className="bg-[#111827] rounded-[32px] shadow-2xl p-8 text-white flex flex-col justify-between">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-blue-500 rounded-lg"><Zap size={18} /></div>
                                <span className="text-[12px] font-black uppercase tracking-widest text-blue-400">{t('systemHealth')}</span>
                            </div>
                            <h3 className="text-[26px] font-black mb-1">Server Status: Optimal</h3>
                            <p className="text-gray-400 text-[14px]">All school modules are functioning normally with 99.9% uptime reported.</p>
                        </div>
                        <div className="mt-8 flex items-center gap-6">
                            <div>
                                <p className="text-[20px] font-black mb-0.5">2.4ms</p>
                                <p className="text-[11px] font-bold text-gray-500 uppercase">Latency</p>
                            </div>
                            <div className="h-8 w-px bg-white/10"></div>
                            <div>
                                <p className="text-[20px] font-black mb-0.5">0.05%</p>
                                <p className="text-[11px] font-bold text-gray-500 uppercase">Error Rate</p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

function Plus({ size, className }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
        >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
    );
}
